/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import InformationCircleIcon from '@atlaskit/icon/core/information-circle';
import { JiraIcon } from '@atlaskit/logo/jira-icon';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';
import { PanelActionBack } from '@atlassian/panel-system/panel-action/back';
import { PanelActionClose } from '@atlassian/panel-system/panel-action/close';
import { PanelActionDown } from '@atlassian/panel-system/panel-action/down';
import { PanelActionExpand } from '@atlassian/panel-system/panel-action/expand';
import { PanelActionGroup } from '@atlassian/panel-system/panel-action/group';
import { PanelActionMore } from '@atlassian/panel-system/panel-action/more';
import { PanelActionNewTab } from '@atlassian/panel-system/panel-action/new-tab';
import { PanelActionUp } from '@atlassian/panel-system/panel-action/up';
import { PanelHeader } from '@atlassian/panel-system/panel-header';
import { PanelTitle } from '@atlassian/panel-system/panel-title';
// Each header variation is wrapped in a bordered box purely for presentation in the docs.
// In a real panel, render `PanelHeader` directly inside a `PanelContainer`.
const styles = cssMap({
	gallery: {
		width: '400px',
	},
	headerWrapper: {
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
});

function HeaderVariant({ children }: { children: React.ReactNode }) {
	return <Box xcss={styles.headerWrapper}>{children}</Box>;
}

export default function Headers(): React.JSX.Element {
	return (
		<Stack space="space.300" xcss={styles.gallery}>
			{/* Basic */}
			<HeaderVariant>
				<PanelHeader>
					<PanelTitle>Header title</PanelTitle>
					<PanelActionGroup>
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>

			{/* Basic + title icon */}
			<HeaderVariant>
				<PanelHeader>
					<PanelTitle icon={<InformationCircleIcon label="" />}>Header title</PanelTitle>
					<PanelActionGroup>
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>

			{/* Branded */}
			<HeaderVariant>
				<PanelHeader>
					<PanelTitle icon={<JiraIcon size="xsmall" appearance="brand" />}>App name</PanelTitle>
					<PanelActionGroup>
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>

			{/* Branded + back button */}
			<HeaderVariant>
				<PanelHeader>
					<PanelActionBack onClick={() => {}} />
					<PanelTitle icon={<JiraIcon size="xsmall" appearance="brand" />}>App name</PanelTitle>
					<PanelActionGroup>
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>

			{/* Branded + panel action */}
			<HeaderVariant>
				<PanelHeader>
					<PanelTitle icon={<JiraIcon size="xsmall" appearance="brand" />}>App name</PanelTitle>
					<PanelActionGroup>
						<PanelActionNewTab href="#" />
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>
			{/* Branded + app panel + 2 panel actions */}
			<HeaderVariant>
				<PanelHeader>
					<PanelTitle icon={<JiraIcon size="xsmall" appearance="brand" />}>App name</PanelTitle>
					<PanelActionGroup>
						<PanelActionMore onClick={() => {}} />
						<PanelActionExpand onClick={() => {}} />
						<PanelActionNewTab href="#" />
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>
			{/* Navigation actions come before all other header actions. */}
			<HeaderVariant>
				<PanelHeader>
					<PanelTitle>Header title</PanelTitle>
					<PanelActionGroup>
						<PanelActionUp onClick={() => {}} />
						<PanelActionDown onClick={() => {}} />
						<PanelActionMore onClick={() => {}} />
						<PanelActionClose onClick={() => {}} />
					</PanelActionGroup>
				</PanelHeader>
			</HeaderVariant>
		</Stack>
	);
}
